import React, { useState, useEffect, useMemo } from 'react';
import { useTable, useFilters, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import { MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import { toast } from 'react-toastify';
import Loading from '../../../components/loading/index.jsx';
import { useNavigate } from 'react-router-dom';

const TeamAnalytics = () => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();

  const fetchSessionData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/auth/sign-in');
        return;
      }
      
      const headers = new Headers();
      headers.set('Authorization', `Token ${token}`);
      
      const response = await fetch('https://staging-sit.com/api/teams/', {
        method: 'GET',
        headers: headers,
        credentials: 'include',
        signal: AbortSignal.timeout(60000)
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/auth/sign-in');
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 500) {
          throw new Error('Server error. The team is working on resolving this issue.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSessions(data);
      setIsLoading(false);
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
      setError(error.message);
      setIsLoading(false);
      toast.error(`Failed to fetch session data: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchSessionData();
  }, [retryCount, navigate]);

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const columns = useMemo(
    () => [
      {
        Header: 'TEAM NAME',
        accessor: 'team_name',
      },
      {
        Header: 'SPORT',
        accessor: 'sport',
        Cell: ({ value }) => capitalizeFirstLetter(value),
      },
      {
        Header: 'CATEGORY',
        accessor: 'team_category',
        Cell: ({ value }) => capitalizeFirstLetter(value),
      },
      {
        Header: 'VIEW ANALYTICS',
        Cell: ({ row }) => (
          <button
            onClick={() => navigate(`/admin/selected-team-analytics/${row.original.id}`)}
            className="bg-[#f46524] text-white px-4 py-2 rounded hover:bg-[#f46570] transition-colors duration-200 dark:bg-[#7551FF] dark:hover:bg-[#5a3cc9]"
          >
            Select
          </button>
        ),
        disableFilters: true,
      },
    ],
    [navigate]
  );

  const data = useMemo(() => {
    return sessions.map(session => ({
      ...session,
      team_name: session.team_name || 'N/A',
      sport: session.sport || 'N/A',
      team_category: session.team_category || 'N/A',
    }));
  }, [sessions]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  if (isLoading) return <Loading />;
  
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden p-6">
        <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-4">Error Occurred</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">{error}</p>
        <div className="space-y-4">
          <button
            onClick={() => setRetryCount(prev => prev + 1)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
          >
            Retry
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('authToken');
              localStorage.removeItem('user');
              navigate('/auth/sign-in');
            }}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
      <div className="p-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">Select Session to View Team Analytics</h2>
        <div className="mb-4">
          <input
            value={globalFilter || ''}
            onChange={e => setGlobalFilter(e.target.value)}
            placeholder="Search teams..."
            className="w-full p-2 border rounded mb-4 dark:border-gray-600 dark:text-gray-200 dark:bg-gray-700"
          />
        </div>
        <div className="overflow-x-auto">
          <table {...getTableProps()} className="min-w-full divide-y divide-gray-200 dark:divide-[#7551FF]">
            <thead className="bg-gray-50 dark:bg-[#7551FF]">
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      {column.render('Header')}
                      <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? <MdArrowDownward className="inline ml-1" />
                            : <MdArrowUpward className="inline ml-1" />
                          : ''}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-[#7551FF]">
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr 
                    {...row.getRowProps()} 
                    className="hover:bg-gray-100 dark:hover:bg-[#7551FF] cursor-pointer transition-all duration-200 ease-in-out"
                  >
                    {row.cells.map(cell => (
                      <td 
                        {...cell.getCellProps()} 
                        className="px-6 py-4 whitespace-nowrap text-gray-800 dark:text-gray-200"
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <span className="text-gray-700 dark:text-gray-300">
              Page{' '}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>{' '}
            </span>
            <select
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              className="ml-2 border rounded dark:border-gray-600 dark:text-gray-200 dark:bg-gray-700"
            >
              {[10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
          <div>
            <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="mr-2 px-3 py-1 border rounded dark:border-gray-600 dark:text-gray-300 disabled:opacity-50">
              {'<<'}
            </button>
            <button onClick={() => previousPage()} disabled={!canPreviousPage} className="mr-2 px-3 py-1 border rounded dark:border-gray-600 dark:text-gray-300 disabled:opacity-50">
              {'<'}
            </button>
            <button onClick={() => nextPage()} disabled={!canNextPage} className="mr-2 px-3 py-1 border rounded dark:border-gray-600 dark:text-gray-300 disabled:opacity-50">
              {'>'}
            </button>
            <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} className="px-3 py-1 border rounded dark:border-gray-600 dark:text-gray-300 disabled:opacity-50">
              {'>>'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamAnalytics;